import type {ReactNode, JSX} from 'react'
import {useState, createContext, useEffect, useContext} from 'react'
import type {NextRouter} from 'next/router'
import {useRouter} from 'next/router'
import {setCookie, getCookie} from 'cookies-next'
import Script from 'next/script'
import {Locales} from '@elanco/rebate-components'
import type {IContentItem} from '@kontent-ai/delivery-sdk'
import type {CdcUser, Data} from '@elanco/cdc-server-utils'
import type {AppPermission} from '@elanco/cdc-server-utils/dist/models/CdcUserModels/Data'
import type {CurrentUserContext} from '@/models/current-user-context'
import {getUserCookieExpirationDate} from '@/utilities/cookies'
import {env} from '@/services/env/client'
import {
	areRolesSufficient,
	doesUserHaveSufficientRoles,
	getUserRoles,
} from '@/products/gated-content/auth'
import type {
	AdvancedPageTemplateContentItem,
	Block,
	GlobalConfigContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
import type {
	AuthNavItemLinkContentItem,
	NavItemLinkContentItem,
} from '@/products/flexible-web-toolkit/components/header/models'
import {appPermissionMapping} from '@/products/auth/app-permissions'
import {logMessage, logError} from '@/services/logger/client'
import {http} from '@/services/http'
import type {UserDetails} from '@/services/cdc/model'
import {getAuthenticationRequirements, isBlockVisible} from './util'
import {AuthenticationGateLevel} from './types'
import {SiteBannerSelfValidationBlock} from './self-banner-self-validation/site-banner-self-validation'

export interface UserCookie {
	id: string
	v: boolean
}

export function logout(router: NextRouter, redirectUrl: string): void {
	window.gigya?.accounts.logout({
		callback: () => {
			setCookie('cdc_user', null)
			if (router.asPath === '/') {
				window.location.reload()
			} else {
				void router.push(redirectUrl)
			}
		},
	})
}

const AuthContext = createContext<CurrentUserContext | undefined>(undefined)

export interface Auth {
	isLoggedIn: boolean
	isValidated: boolean
	isSelfValidated: boolean
}

export const useAuth = (): CurrentUserContext => {
	const authContext = useContext(AuthContext)
	if (authContext === undefined) {
		throw new Error('useAuth must be used inside an AuthProvider')
	}
	return authContext
}

interface AuthProviderProps {
	page: Tersed<AdvancedPageTemplateContentItem>
	globalConfig: Tersed<GlobalConfigContentItem>
	auth: Auth
	children: ReactNode
}

export const AuthProvider = ({
	page,
	globalConfig,
	auth,
	children,
}: AuthProviderProps): JSX.Element => {
	// Set initial state from server-side checks
	const [authenticated, setAuthenticated] = useState<boolean>(auth.isLoggedIn)
	const [validated, setValidated] = useState<boolean>(auth.isValidated)
	const [selfValidated, setSelfValidated] = useState<boolean>(
		auth.isSelfValidated
	)
	const [authLevel, setAuthLevel] = useState<number>(-1)
	const [userDetails, setUserDetails] = useState<UserDetails>({
		id: null,
		firstName: '',
		lastName: '',
		emailAddress: '',
		telephone: null,
		practiceName: null,
		practicePostcode: null,
		crmId: null,
		light: null,
		appPermissions: null,
		userRoles: null,
		salutation: '',
	})
	const [ready, setReady] = useState<boolean>(false)
	const router = useRouter()
	const gateLevel = getAuthenticationRequirements(page, globalConfig)

	const {loginUrl, validationErrorUrl, cdcLang} = globalConfig.elements
	const locale = cdcLang || router.locale
	const formattedPath = router.asPath === '/' ? '' : router.asPath

	useEffect(() => {
		let level = 0
		if (selfValidated) level = 1
		if (authenticated) level = 2
		if (validated) level = 3
		setAuthLevel(level)

		logMessage(`User authentication set to level ${level}`)
	}, [selfValidated, authenticated, validated])

	const shouldShowPage = (): boolean => {
		switch (gateLevel) {
			case 'Validated':
				return validated
			case 'Authenticated':
				return authenticated || validated
			default:
				return true
		}
	}

	const authenticateUser = (
		isLoggedIn: boolean,
		userHasNecessaryAppPermissions: boolean
	): void => {
		setAuthenticated(isLoggedIn)
		setSelfValidated(auth.isSelfValidated || isLoggedIn)
		setValidated(userHasNecessaryAppPermissions)
		//To add CDC flow builder only for German vet hybris users
		if (
			env.NEXT_PUBLIC_TOWER === 'vet' &&
			isLoggedIn &&
			router.locale === 'de'
		) {
			if (!window.gigya) return

			const flow = window.gigya.flow('vet_hybris_profile_update')
			//start executing the flow
			flow.execute()
		}
		if (
			isLoggedIn &&
			(router.asPath === '/login' || router.asPath === '/register')
		) {
			logMessage(`User with uid ${userDetails.id} logged in successfully`)
			void router.replace(`/`)
		}
		if (
			!isLoggedIn &&
			(gateLevel === 'Authenticated' || gateLevel === 'Validated')
		) {
			void router.replace(
				`/${router.locale ?? ''}${loginUrl}?returnUrl=${encodeURIComponent(
					`/${router.locale ?? ''}${router.asPath}`
				)}`
			)
			return
		}

		if (!userHasNecessaryAppPermissions && gateLevel === 'Validated') {
			const unauthenticatedRedirectUrl =
				page.elements.snippetPageSeoUnauthenticatedRedirectUrl
			if (unauthenticatedRedirectUrl) {
				window.location.href = unauthenticatedRedirectUrl
			} else {
				void router.replace(
					`/${router.locale ?? ''}${validationErrorUrl}`
				)
			}
		}
	}

	const authenticateUserRole = (userHasNecessaryRoles: boolean): void => {
		setValidated(userHasNecessaryRoles)
		if (!userHasNecessaryRoles && gateLevel === 'Validated') {
			logMessage(
				`Failed to authenticate user role with uid ${userDetails.id} and redirected to unauthenticatedRoleRedirectUrl`
			)
			const unauthenticatedRoleRedirectUrl =
				page.elements.snippetPageSeoUnauthenticatedRoleRedirectUrl
			if (unauthenticatedRoleRedirectUrl) {
				window.location.href = unauthenticatedRoleRedirectUrl
			} else {
				void router.replace(
					`/${router.locale ?? ''}${validationErrorUrl}`
				)
			}
		}
	}

	const loadTowerAndCheckPermissions = (
		pageAppPermissions: string[] | undefined,
		userAppPermissions: AppPermission | undefined | null
	): boolean => {
		logMessage(
			`Checking if the page app permissions are set for page ${locale}${formattedPath}`
		)
		if (!pageAppPermissions || pageAppPermissions.length === 0) {
			logMessage(
				`No page app permissions specified for page: /${locale}${formattedPath} `
			)
			return false
		}
		if (!userAppPermissions) {
			logMessage(
				`No app permissions found for user with uid: ${userDetails.id} `
			)
			return false
		}

		const mapped = pageAppPermissions.map((codename) => {
			return appPermissionMapping.get(codename)
		})

		return mapped.every(
			(appPermissionField) =>
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- appPermissionField wont be null
				userAppPermissions[appPermissionField!]
					?.toString()
					.toLowerCase() === 'true'
		)
	}

	const doesUserHaveNecessaryAppPermission = (
		userAppPermissions: AppPermission | undefined
	): boolean => {
		if (gateLevel !== 'Validated') {
			return false
		}
		const pageAppPermissions =
			page.elements.authenticationGateApplicationPermissions.map(
				(a) => a.codename
			)
		const pageRoles = page.elements.authenticationGateUserRoles.map(
			(a) => a.codename
		)
		if (pageAppPermissions.length === 0 && pageRoles.length === 0) {
			return false
		}
		if (pageAppPermissions.length === 0 && pageRoles.length >= 0) {
			return true
		}

		const hasPermission = loadTowerAndCheckPermissions(
			pageAppPermissions,
			userAppPermissions
		)
		logMessage(
			`User with the uid${userDetails.id} ${
				hasPermission ? 'have' : "don't have"
			} app permission for page:/${locale}${formattedPath}`
		)

		return hasPermission
	}

	const doesUserHaveNecessaryRole = (user: Data): boolean => {
		logMessage(
			`Checking if user with uid ${userDetails.id} has necessary roles to view the content of page: /${locale}${formattedPath}`
		)
		if (gateLevel !== 'Validated') {
			return false
		}

		const pageRoles = page.elements.authenticationGateUserRoles.map(
			(a) => a.codename
		)

		return doesUserHaveSufficientRoles(pageRoles, user)
	}

	const checkUserRole = (
		item: Tersed<NavItemLinkContentItem | AuthNavItemLinkContentItem>
	): boolean => {
		if (!('authenticationGateLevel' in item.elements)) {
			return true
		}
		const authLink = item as Tersed<AuthNavItemLinkContentItem>

		const itemGateLevel = authLink.elements.authenticationGateLevel[0]?.name
		if (
			!itemGateLevel ||
			itemGateLevel !== AuthenticationGateLevel.Validated
		) {
			return true
		}
		const userRoleRequired =
			authLink.elements.authenticationGateUserRoles.map(
				({codename}) => codename
			)
		const userRoles = userDetails.userRoles ?? []
		return areRolesSufficient(userRoleRequired, userRoles)
	}

	const checkAppPermission = (
		item: Tersed<NavItemLinkContentItem | AuthNavItemLinkContentItem>
	): boolean => {
		if (!('authenticationGateLevel' in item.elements)) {
			return true
		}

		const authLink = item as Tersed<AuthNavItemLinkContentItem>

		const itemGateLevel = authLink.elements.authenticationGateLevel[0]?.name
		if (
			!itemGateLevel ||
			itemGateLevel !== AuthenticationGateLevel.Validated
		) {
			return true
		}

		const appPermissions =
			authLink.elements.authenticationGateApplicationPermissions.map(
				(perm) => perm.codename
			)
		const userAppPermissions = userDetails.appPermissions
		return loadTowerAndCheckPermissions(appPermissions, userAppPermissions)
	}

	const checkUserRoleForBlock = <TContentItem extends IContentItem>(
		item: Parameters<Block<TContentItem>>['0']['block']
	): boolean => {
		const allowedUserRoles = (
			item.elements.authenticationGateComponentUserRoles ?? []
		).map((role) => role.codename)
		const loggedInUserRoles = userDetails.userRoles ?? []
		return areRolesSufficient(allowedUserRoles, loggedInUserRoles)
	}

	const handleUserDetails = (
		uid: string,
		lightUser: boolean,
		profile?: Profile,
		user?: Data
	): void => {
		if (profile && user) {
			const userHasNecessaryAppPermission =
				doesUserHaveNecessaryAppPermission(user.appPermission)
			const userHasNecessaryRole = doesUserHaveNecessaryRole(user)

			setUserDetails({
				id: uid,
				firstName:
					typeof profile.firstName === 'string'
						? profile.firstName
						: '',
				lastName:
					typeof profile.lastName === 'string'
						? profile.lastName
						: '',
				emailAddress:
					typeof profile.email === 'string' ? profile.email : '',
				telephone:
					(profile.phones as {number?: string}[] | undefined)?.[0]
						?.number ?? null,
				practiceName:
					typeof user.business?.name === 'string'
						? user.business.name
						: null,
				practicePostcode:
					typeof user.business?.zip === 'string'
						? user.business.zip
						: null,
				crmId:
					locale === 'en' || locale === Locales.UK
						? ((user.payTo as string | null | undefined) ?? null)
						: ((user.business?.crmId as
								| string
								| null
								| undefined) ?? null),
				light: lightUser,
				appPermissions: user.appPermission ?? null,
				userRoles: getUserRoles(user),
				salutation:
					typeof user.salutation === 'string' ? user.salutation : '',
			})
			if (lightUser) {
				const newCookie = {
					id: uid,
					email:
						typeof profile.email === 'string' ? profile.email : '',
					light: true,
					v: userHasNecessaryAppPermission,
				}
				setCookie('cdc_user', btoa(JSON.stringify(newCookie)), {
					expires: getUserCookieExpirationDate(),
				})
			} else if (!getCookie('cdc_user')) {
				const newCookie = {
					id: uid,
					v: userHasNecessaryAppPermission,
				}
				setCookie('cdc_user', btoa(JSON.stringify(newCookie)), {
					expires: getUserCookieExpirationDate(),
				})
			}

			authenticateUser(true, userHasNecessaryAppPermission)
			authenticateUserRole(userHasNecessaryRole)
		} else {
			authenticateUser(false, false)
			setCookie('cdc_user', null)
		}
	}

	const getAppPermissions = (
		accountInfo: CdcUser
	): Promise<AppPermission | null> => {
		const permissionsUrl = '/api/user/appPermissions'
		const requestUrl = new URL(permissionsUrl, 'http://localhost')
		requestUrl.searchParams.set('uid', accountInfo.UID)
		requestUrl.searchParams.set('locale', locale ?? '')
		const appPermissionsPath = `${requestUrl.pathname}${requestUrl.search}`
		return http
			.get<AppPermission | null>(appPermissionsPath)
			.then(({data}) => {
				const permissionEntries = data
					? Object.entries(data).filter(([, value]) => value === true)
					: []
				logMessage('Fetching app permissions', {
					url: permissionsUrl,
					locale,
					permissions: permissionEntries,
				})
				return data
			})
			.catch(() => {
				logError(
					new Error(
						`Failed to make a request to /api/user/appPermissions`
					)
				)
				return null
			})
	}

	const accountInfoCallback = (accountInfo?: CdcUser): void => {
		if (accountInfo) {
			if (accountInfo.UID) {
				void getAppPermissions(accountInfo).then((appPermissions) => {
					const updatedAccountInfo = accountInfo
					if (appPermissions) {
						updatedAccountInfo.data.appPermission = {
							...accountInfo.data.appPermission,
							...appPermissions,
						}
					}

					handleUserDetails(
						updatedAccountInfo.UID,
						false,
						updatedAccountInfo.profile,
						updatedAccountInfo.data
					)
				})
			}

			handleUserDetails(
				accountInfo.UID,
				false,
				accountInfo.profile,
				accountInfo.data
			)
		}
	}

	useEffect(() => {
		if (ready) {
			window.gigya?.accounts.getAccountInfo({
				callback: accountInfoCallback,
				extraProfileFields: 'phones',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional
	}, [ready, router.asPath])

	return (
		<AuthContext.Provider
			value={{
				authenticated,
				validated,
				selfValidated,
				authLevel,
				userDetails,
				handleUserDetails,
				checkAppPermission,
				checkUserRole,
				checkUserRoleForBlock,
				isBlockVisible: (block) => isBlockVisible(authenticated, block),
				ready,
			}}
		>
			<div data-testid="auth-provider">
				{globalConfig.elements.cdcKey ? (
					<Script
						id={`${globalConfig.elements.cdcKey}-gigya-page`}
						onReady={() => {
							window.onGigyaServiceReady = () => {
								setReady(true)
								window.gigya?.accounts.getAccountInfo({
									callback: accountInfoCallback,
									extraProfileFields: 'phones',
								})
							}
						}}
						src={`https://cdns.gigya.com/js/gigya.js?apikey=${
							globalConfig.elements.cdcKey
						}&lang=${locale ?? ''}`}
					/>
				) : null}
				<input
					id="CDC-status"
					type="hidden"
					value={ready ? 'Ready' : 'Loading'}
				/>
				{shouldShowPage() && (
					<>
						{(page.elements.authenticationGateLevel[0]?.name ===
							'1 - Self-Confirmation Required' ||
							(!page.elements.authenticationGateLevel[0]?.name &&
								globalConfig.elements.authenticationGateLevel[0]
									?.name ===
									'1 - Self-Confirmation Required')) &&
						globalConfig.elements.selfValidationPopup[0] &&
						!selfValidated ? (
							// @ts-expect-error -- No context is ok
							<SiteBannerSelfValidationBlock
								block={
									// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- disabled as every page does not have pageSiteBanner
									page.elements.pageSiteBanner?.[0]
										? page.elements.pageSiteBanner[0]
										: globalConfig.elements
												.selfValidationPopup[0]
								}
								globalConfig={globalConfig}
								page={page}
							/>
						) : null}
						{children}
					</>
				)}
			</div>
		</AuthContext.Provider>
	)
}
